/**
 * V2.0.65 — Chat latency dashboard card for the Admin Console.
 *
 * Reads `/api/admin/chat-stats?hours=24` and renders:
 *   - Total requests + error rate badge
 *   - Per-mode (simple / pro) latency: p50, p95, p99
 *   - 24-hour bar sparkline of p95 over time
 *
 * No external chart dep — CSS bars. Stays under 50 LOC of actual layout.
 */
import { useEffect, useState } from "react";
import { Loader2, Activity, AlertTriangle } from "lucide-react";
import { getChatStats } from "../lib/api";

const fmtMs = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const fmtHour = (iso) => {
  const d = new Date(iso);
  return `${String(d.getUTCHours()).padStart(2, "0")}:00`;
};

const ModeColumn = ({ label, stats }) => {
  if (!stats || stats.count === 0) {
    return (
      <div data-testid={`chat-stats-mode-${label}`} className="flex-1">
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
          {label}
        </div>
        <div className="text-zinc-600 text-xs">No traffic yet</div>
      </div>
    );
  }
  return (
    <div data-testid={`chat-stats-mode-${label}`} className="flex-1 space-y-1">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
        {label} · {stats.count} req
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className="text-zinc-500 text-[9px]">p50</div>
          <div className="text-zinc-100 text-sm font-mono">{fmtMs(stats.p50_ms)}</div>
        </div>
        <div>
          <div className="text-zinc-500 text-[9px]">p95</div>
          <div className={`text-sm font-mono ${stats.p95_ms > 60000 ? "text-rose-300" : stats.p95_ms > 30000 ? "text-amber-300" : "text-emerald-300"}`}>
            {fmtMs(stats.p95_ms)}
          </div>
        </div>
        <div>
          <div className="text-zinc-500 text-[9px]">p99</div>
          <div className="text-zinc-100 text-sm font-mono">{fmtMs(stats.p99_ms)}</div>
        </div>
      </div>
    </div>
  );
};

const HourlySparkline = ({ hourly }) => {
  if (!hourly || hourly.length === 0) {
    return (
      <div className="text-zinc-600 text-xs italic">
        No traffic in the selected window.
      </div>
    );
  }
  const maxP95 = Math.max(...hourly.map((h) => h.p95_ms), 1);
  return (
    <div className="flex items-end gap-0.5 h-12" data-testid="chat-stats-sparkline">
      {hourly.map((h) => {
        const pct = Math.min(100, (h.p95_ms / maxP95) * 100);
        const dangerClass = h.p95_ms > 60000 ? "bg-rose-400" : h.p95_ms > 30000 ? "bg-amber-400" : "bg-emerald-400";
        return (
          <div
            key={h.hour}
            title={`${fmtHour(h.hour)} · ${h.count} req · p95 ${fmtMs(h.p95_ms)}`}
            className={`flex-1 min-w-[6px] ${dangerClass} opacity-80 hover:opacity-100 transition-opacity`}
            style={{ height: `${Math.max(8, pct)}%` }}
          />
        );
      })}
    </div>
  );
};

export default function ChatStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);

  const load = async (h = hours) => {
    setLoading(true);
    try {
      const r = await getChatStats(h);
      setStats(r.data);
    } catch (err) {
      console.debug("[AdminConsole] chat-stats fetch failed:", err);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(hours);
    // Auto-refresh every 60s while the page is open.
    const t = setInterval(() => load(hours), 60000);
    return () => clearInterval(t);
  }, [hours]);

  return (
    <div
      data-testid="admin-chat-stats-card"
      className="mb-6 border border-zinc-800 bg-zinc-950/60 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-brass-400" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
            Chat latency · last
          </div>
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            data-testid="chat-stats-window"
            className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs font-mono px-2 py-0.5"
          >
            <option value={1}>1h</option>
            <option value={6}>6h</option>
            <option value={24}>24h</option>
            <option value={72}>72h</option>
            <option value={168}>7d</option>
          </select>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-500">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
          {stats && (
            <>
              <span data-testid="chat-stats-total">{stats.total_requests} req</span>
              <span
                data-testid="chat-stats-error-rate"
                className={`inline-flex items-center gap-1 ${stats.error_rate > 0.05 ? "text-rose-300" : "text-zinc-500"}`}
              >
                {stats.error_rate > 0.05 && <AlertTriangle className="w-3 h-3" />}
                {(stats.error_rate * 100).toFixed(1)}% err
              </span>
            </>
          )}
        </div>
      </div>

      {stats ? (
        <>
          <div className="flex gap-6 mb-4">
            <ModeColumn label="simple" stats={stats.by_mode?.simple} />
            <ModeColumn label="pro" stats={stats.by_mode?.pro} />
          </div>
          <HourlySparkline hourly={stats.hourly} />
        </>
      ) : (
        <div className="text-zinc-600 text-xs italic">
          {loading ? "Loading…" : "No data."}
        </div>
      )}
    </div>
  );
}
